import { App as CapacitorApp } from '@capacitor/app';
import OCGatewayClient from './OCGatewayClient';
import i18n, { supportedLanguages } from '../i18n';
import { DEFAULT_PUSH_NOTIFICATION_CONFIG } from '../models/PushNotificationConfig';
import {
    PushNotificationContext,
    setCachedPushNotificationConfig,
    setPushNotificationContext
} from '../query/pushNotificationConfigQuery';

const fallbackLocale = DEFAULT_PUSH_NOTIFICATION_CONFIG.locale;
const fallbackTimezone = DEFAULT_PUSH_NOTIFICATION_CONFIG.quietHours.timezone;
const appNotificationLocales = supportedLanguages.filter((language) => language !== 'dev');

let installed = false;
let lastSentContextKey: string | null = null;
let syncInProgress: Promise<void> | null = null;
let pendingSyncRequested = false;

export function installPushNotificationContextSync() {
    if (installed) {
        return;
    }

    OCGatewayClient.socket.on('connect', () => {
        void syncPushNotificationContext();
    });

    i18n.on('languageChanged', () => {
        void syncPushNotificationContext();
    });

    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
            void syncPushNotificationContext();
        }
    });

    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            void syncPushNotificationContext();
        }
    });

    void syncPushNotificationContext();

    installed = true;
}

export async function syncPushNotificationContext() {
    if (!OCGatewayClient.socket.connected) {
        return;
    }

    const context = getCurrentPushNotificationContext();
    const contextKey = JSON.stringify(context);
    if (contextKey === lastSentContextKey) {
        return;
    }
    if (syncInProgress) {
        pendingSyncRequested = true;
        return syncInProgress ?? undefined;
    }

    syncInProgress = (async () => {
        try {
            const updatedConfig = await setPushNotificationContext(context);
            setCachedPushNotificationConfig(updatedConfig);
            lastSentContextKey = contextKey;
        } catch (error) {
            console.warn('(OCPushNotificationContextSync) Failed to sync push notification context', error);
        } finally {
            syncInProgress = null;
            if (pendingSyncRequested) {
                pendingSyncRequested = false;
                void syncPushNotificationContext();
            }
        }
    })();

    return syncInProgress;
}

function getCurrentPushNotificationContext(): PushNotificationContext {
    return {
        locale: getNotificationLocale(),
        timezone: getLocalTimezone()
    };
}

function getNotificationLocale() {
    const language = i18n.resolvedLanguage ?? i18n.language ?? navigator.language ?? fallbackLocale;
    const baseLanguage = language.split('-')[0].toLowerCase();

    return appNotificationLocales.includes(baseLanguage as typeof appNotificationLocales[number])
        ? baseLanguage
        : fallbackLocale;
}

function getLocalTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimezone;
    } catch {
        return fallbackTimezone;
    }
}
